/**
 * The ONE JustCall dialer for the whole page: an iframe of JustCall's own web dialer, which the rep
 * signs in to and which places, controls and hangs up the call. JustCall offers no programmable
 * calling SDK — its "Dialer SDK" (`@justcall/justcall-dialer-sdk`) is just this iframe plus a
 * postMessage protocol, re-implemented here so we can:
 *
 *  - keep a single iframe alive across calls and page navigations (the SDK builds a new one per
 *    instance, and moving or re-creating an iframe reloads the dialer, which would drop a live
 *    call). `JustCallDock` hosts it; every `JustCallSoftphoneClient` shares it;
 *  - check `event.origin` / `event.source` on incoming messages (the SDK's listener checks
 *    neither, so any window could post fake call events into it).
 *
 * Protocol (from the SDK source, v1.4.2): we post `{ type: 'dial-number', phoneNumber }` and
 * `{ type: 'is-logged-in' }` to the iframe; it posts back `{ name, data }` messages named
 * `is-logged-in` (data "true"/"false"), `logged-in-status` ({ logged_in }), `call-ringing`
 * ({ direction, from, to, call_sid }), `call-answered` ({ call_sid, direction }) and `call-ended`
 * ({ call_sid, direction, duration }).
 */

export const JUSTCALL_ORIGIN = 'https://app.justcall.io';
export const JUSTCALL_DIALER_URL = `${JUSTCALL_ORIGIN}/dialer`;
/**
 * Same permissions the SDK grants its iframe. The embedding page must also allow JustCall to use
 * the mic in its own Permissions-Policy header (dashboard: `apps/nextjs/next.config.ts`).
 */
export const JUSTCALL_IFRAME_ALLOW = 'microphone; autoplay; clipboard-read; clipboard-write;';

export type JustCallCallEventName = 'call-ringing' | 'call-answered' | 'call-ended';

export interface JustCallCallEvent {
  name: JustCallCallEventName;
  callSid: string | null;
  direction: 'outgoing' | 'incoming' | null;
  /** The dialed number (ringing only), as JustCall formats it. */
  to: string | null;
}

export interface JustCallDialerState {
  /** Something asked for the dialer — the dock renders (and loads the iframe) from then on. */
  active: boolean;
  /** The dock is open (dialer visible) rather than collapsed to its pill. */
  expanded: boolean;
  /** The iframe has loaded. */
  ready: boolean;
  /** Last sign-in state JustCall reported; null until it has said. */
  loggedIn: boolean | null;
}

const INITIAL_STATE: JustCallDialerState = { active: false, expanded: false, ready: false, loggedIn: null };

const CALL_EVENT_NAMES: ReadonlySet<string> = new Set<JustCallCallEventName>([
  'call-ringing',
  'call-answered',
  'call-ended',
]);

function isCallEventName(name: unknown): name is JustCallCallEventName {
  return typeof name === 'string' && CALL_EVENT_NAMES.has(name);
}

function readString(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  return typeof value === 'string' && value !== '' ? value : null;
}

function readDirection(record: Record<string, unknown>): 'outgoing' | 'incoming' | null {
  const value = readString(record, 'direction')?.toLowerCase() ?? '';
  if (value.startsWith('out')) return 'outgoing';
  if (value.startsWith('in')) return 'incoming';
  return null;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value != null && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

export class JustCallDialerBridge {
  private state: JustCallDialerState = INITIAL_STATE;
  private readonly stateListeners = new Set<() => void>();
  private readonly callListeners = new Set<(event: JustCallCallEvent) => void>();
  private iframe: HTMLIFrameElement | null = null;
  private readyWaiters: Array<() => void> = [];
  private loginCheckWaiters: Array<(loggedIn: boolean) => void> = [];
  private readonly dialerUrl: string;
  private readonly origin: string;

  /** The URL/origin are injectable for tests only; the app always uses JustCall's. */
  constructor(params?: { dialerUrl?: string; origin?: string }) {
    this.dialerUrl = params?.dialerUrl ?? JUSTCALL_DIALER_URL;
    this.origin = params?.origin ?? JUSTCALL_ORIGIN;
  }

  // ── Store (useSyncExternalStore) ────────────────────────────────────────────────────────────

  subscribe = (listener: () => void): (() => void) => {
    this.stateListeners.add(listener);
    return () => this.stateListeners.delete(listener);
  };

  getSnapshot = (): JustCallDialerState => this.state;

  getServerSnapshot = (): JustCallDialerState => INITIAL_STATE;

  private setState(patch: Partial<JustCallDialerState>): void {
    const next = { ...this.state, ...patch };
    if ((Object.keys(patch) as Array<keyof JustCallDialerState>).every((k) => next[k] === this.state[k])) return;
    this.state = next;
    this.stateListeners.forEach((listener) => listener());
  }

  /** Listen for JustCall call events. Returns the unsubscribe. */
  onCallEvent(listener: (event: JustCallCallEvent) => void): () => void {
    this.callListeners.add(listener);
    return () => this.callListeners.delete(listener);
  }

  // ── Dock visibility ─────────────────────────────────────────────────────────────────────────

  /** Ask for the dialer: the dock mounts and loads it. `expand` also opens it. */
  activate(options: { expand: boolean }): void {
    this.setState(options.expand ? { active: true, expanded: true } : { active: true });
  }

  expand(): void {
    this.setState({ active: true, expanded: true });
  }

  collapse(): void {
    this.setState({ expanded: false });
  }

  // ── Iframe lifecycle (driven by JustCallDock) ───────────────────────────────────────────────

  /**
   * Host the iframe in `host`, creating it on first use. Re-attaching to the same host is a no-op —
   * moving the iframe would reload JustCall's dialer.
   */
  attach(host: HTMLElement): void {
    if (this.iframe == null) {
      const iframe = document.createElement('iframe');
      iframe.src = this.dialerUrl;
      iframe.allow = JUSTCALL_IFRAME_ALLOW;
      iframe.title = 'JustCall dialer';
      iframe.style.border = '0';
      iframe.style.width = '100%';
      iframe.style.height = '100%';
      iframe.addEventListener('load', this.handleLoad);
      window.addEventListener('message', this.handleMessage);
      this.iframe = iframe;
    }
    if (this.iframe.parentElement !== host) host.appendChild(this.iframe);
  }

  /** The dock is going away: drop the iframe (and with it any sign-in state we knew about). */
  detach(host: HTMLElement): void {
    if (this.iframe == null || this.iframe.parentElement !== host) return;
    this.iframe.removeEventListener('load', this.handleLoad);
    window.removeEventListener('message', this.handleMessage);
    this.iframe.remove();
    this.iframe = null;
    this.setState({ ready: false, loggedIn: null });
  }

  /** Resolve once the iframe has loaded; reject after `timeoutMs`. */
  whenReady(timeoutMs: number): Promise<void> {
    if (this.state.ready) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.readyWaiters = this.readyWaiters.filter((w) => w !== done);
        reject(new Error('JustCall dialer did not load'));
      }, timeoutMs);
      const done = () => {
        clearTimeout(timer);
        resolve();
      };
      this.readyWaiters.push(done);
    });
  }

  // ── Commands ────────────────────────────────────────────────────────────────────────────────

  /**
   * Ask JustCall whether the rep is signed in. Resolves null when it doesn't answer in time — the
   * reply isn't documented for every state, so callers treat null as "go ahead and try".
   */
  checkLoggedIn(timeoutMs: number): Promise<boolean | null> {
    const target = this.iframe?.contentWindow;
    if (target == null || !this.state.ready) return Promise.resolve(null);
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.loginCheckWaiters = this.loginCheckWaiters.filter((w) => w !== done);
        resolve(null);
      }, timeoutMs);
      const done = (loggedIn: boolean) => {
        clearTimeout(timer);
        resolve(loggedIn);
      };
      this.loginCheckWaiters.push(done);
      target.postMessage({ type: 'is-logged-in' }, this.origin);
    });
  }

  /** Hand JustCall a number to call. */
  dial(phoneNumber: string): void {
    const target = this.iframe?.contentWindow;
    if (target == null) throw new Error('The JustCall dialer isn’t open');
    target.postMessage({ type: 'dial-number', phoneNumber }, this.origin);
  }

  // ── Inbound ─────────────────────────────────────────────────────────────────────────────────

  private readonly handleLoad = (): void => {
    this.setState({ ready: true });
    const waiters = this.readyWaiters;
    this.readyWaiters = [];
    waiters.forEach((w) => w());
    // Learn the sign-in state up front so the dock / settings can show it before any call.
    void this.checkLoggedIn(3000);
  };

  private readonly handleMessage = (event: MessageEvent): void => {
    if (event.origin !== this.origin || this.iframe == null || event.source !== this.iframe.contentWindow) return;
    const message = asRecord(event.data);
    const name = message.name;
    const data = message.data;

    if (name === 'is-logged-in') {
      const loggedIn = data === true || data === 'true';
      this.setState({ loggedIn });
      const waiters = this.loginCheckWaiters;
      this.loginCheckWaiters = [];
      waiters.forEach((w) => w(loggedIn));
      return;
    }
    if (name === 'logged-in-status') {
      this.setState({ loggedIn: asRecord(data).logged_in === true });
      return;
    }
    if (!isCallEventName(name)) return;

    const record = asRecord(data);
    const callEvent: JustCallCallEvent = {
      name,
      callSid: readString(record, 'call_sid'),
      direction: readDirection(record),
      to: readString(record, 'to'),
    };
    // Someone is calling the rep: open the dock so they can answer it.
    if (name === 'call-ringing' && callEvent.direction === 'incoming') this.expand();
    this.callListeners.forEach((listener) => listener(callEvent));
  };
}

let sharedBridge: JustCallDialerBridge | null = null;

/** The page-wide bridge (created on first use, browser-only). */
export function getJustCallDialerBridge(): JustCallDialerBridge {
  sharedBridge ??= new JustCallDialerBridge();
  return sharedBridge;
}
